/**
 * `CompetenceAdmin` — pagina admin per la gestione delle Competenze (CRUD + N:N).
 *
 * Design ref: "Componenti chiave" → pagine admin; rotta `/admin/competences`
 * (admin: gestione competenze, 5.1). La relazione Competenza↔Skill è
 * molti‑a‑molti (5.3): qui si associa una Competenza a una o più Skill
 * (5.5) tramite `skillIds`.
 *
 * CRUD su `/api/competences`:
 *  - `GET /api/competences`   → elenco (con `skillIds`);
 *  - `POST /api/competences`  → creazione (`{ name, description?, mission?, departmentId?, skillIds? }`);
 *  - `PUT /api/competences/:id` → aggiornamento (nome/descrizione/associazioni);
 *  - `DELETE /api/competences/:id` → cancellazione.
 *
 * L'elenco è presentato con il componente generico `DataTable` (ordinabile,
 * paginato) e una ricerca client‑side per nome o descrizione. Creazione e
 * modifica (nome + descrizione + associazioni N:N) avvengono in una `Modal`
 * condivisa; l'eliminazione è un'azione di riga.
 *
 * L'elenco delle Skill (`GET /api/skills`) serve solo a offrire le opzioni di
 * associazione N:N. La coerenza bidirezionale `skills.competenceIds` ↔
 * `competenze.skillIds` è mantenuta dall'API (catalogService); dopo ogni
 * mutazione si invalidano entrambe le query.
 *
 * L'autorizzazione autorevole è dell'API (403 per i non‑admin, 5.8).
 *
 * _Requirements: 5.1, 5.2._
 */
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COMPETENCE_AREA_VALUES,
  type CompetenceArea,
  type CompetenceDto,
  type CreateCompetenceRequest,
  type DepartmentDto,
  type ImportCompetenceRow,
  type ImportCompetencesRequest,
  type ImportCompetencesResponse,
  type Livello,
  type SkillDto,
  type UpdateCompetenceRequest,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { useAuth } from '../auth/AuthProvider';
import { parseCompetencesCsv, CsvParseError } from '../utils/parseCompetencesCsv';
import {
  Alert,
  Button,
  DataTable,
  Field,
  Icon,
  Loading,
  Modal,
  PageHeader,
  TextInput,
  TextArea,
  SkillWeightEditor,
  type Column,
  type SortDirection,
} from '../components/ui';

const competencesKey = ['competences'] as const;
const skillsKey = ['skills'] as const;
const departmentsKey = ['departments'] as const;

/** Confronto di stringhe già orientato per la direzione (per `DataTable`). */
function compareByName(a: CompetenceDto, b: CompetenceDto, direction: SortDirection): number {
  const sign = direction === 'desc' ? -1 : 1;
  const va = a.name.toLowerCase();
  const vb = b.name.toLowerCase();
  if (va < vb) return -sign;
  if (va > vb) return sign;
  return 0;
}

/**
 * `CompetenceLevelBar` — barra a 5 quadratini (i Livelli `e-1..e-5`) che
 * evidenzia in colore primario i livelli **gestiti** dalla competenza, ossia
 * quelli per cui esiste un testo esplicativo (`level1..level5`) nel database.
 *
 * A differenza della barra "Analisi Gap" delle professioni, qui non c'è alcun
 * marker/cursore: i quadratini sono semplicemente colorati (gestito) o vuoti
 * (non gestito), senza confronto tra posseduto e richiesto.
 */
function CompetenceLevelBar({ competence }: { competence: CompetenceDto }) {
  const managed = [
    competence.level1,
    competence.level2,
    competence.level3,
    competence.level4,
    competence.level5,
  ].map((text) => (text ?? '').trim() !== '');

  const count = managed.filter(Boolean).length;
  const ariaLabel =
    count === 0
      ? 'Nessun livello con testo esplicativo'
      : `Livelli gestiti: ${managed
          .map((m, i) => (m ? `e-${i + 1}` : null))
          .filter(Boolean)
          .join(', ')}`;

  return (
    <span className="level-bar" role="img" aria-label={ariaLabel} title={ariaLabel}>
      {[1, 2, 3, 4, 5].map((rank) => (
        <span key={rank} className="level-bar__cell-wrap level-bar__cell-wrap--flush">
          <span
            className={[
              'level-bar__cell',
              managed[rank - 1] ? 'level-bar__cell--managed' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            e-{rank}
          </span>
        </span>
      ))}
    </span>
  );
}

/** Stato di modifica: nome + descrizione + reparto + associazioni correnti. */
interface EditingState {
  id: string;
  name: string;
  description: string;
  /** Missione/scopo della competenza (testo libero facoltativo). */
  mission: string;
  /** Testi esplicativi dei Livelli di proficiency e-CF (e-1..e-5). */
  level1: string;
  level2: string;
  level3: string;
  level4: string;
  level5: string;
  /** Area e-CF selezionata; `''` = nessuna area. */
  area: '' | CompetenceArea;
  /** `''` = nessun reparto. */
  departmentId: string;
  skillIds: string[];
  /** Peso `1..5` per skill (chiave = skill id); le skill assenti valgono 1. */
  skillWeights: Record<string, number>;
  /**
   * Livello richiesto per skill (chiave = skill id). Le skill assenti non
   * hanno un Livello_Richiesto (nessun default forzato).
   */
  skillRequiredLevels: Record<string, Livello>;
}

export default function CompetenceAdmin() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { role, me } = useAuth();
  // Permessi lato API per le competenze:
  // - `admin`: CRUD completo su tutte le competenze;
  // - `manager`: può SOLO modificare (`PUT`) le competenze del PROPRIO reparto;
  //   non può creare, importare, eliminare né cambiarne il reparto.
  // La UI riflette questi permessi: nasconde le azioni non consentite e, per il
  // manager, limita la vista alle competenze del suo reparto.
  const isAdmin = role === 'admin';
  const managerDepartmentId = role === 'manager' ? me?.departmentId ?? null : null;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newMission, setNewMission] = useState('');
  const [newLevel1, setNewLevel1] = useState('');
  const [newLevel2, setNewLevel2] = useState('');
  const [newLevel3, setNewLevel3] = useState('');
  const [newLevel4, setNewLevel4] = useState('');
  const [newLevel5, setNewLevel5] = useState('');
  const [newArea, setNewArea] = useState<'' | CompetenceArea>('');
  const [newDepartmentId, setNewDepartmentId] = useState('');
  const [newSkillIds, setNewSkillIds] = useState<string[]>([]);
  const [newSkillWeights, setNewSkillWeights] = useState<Record<string, number>>({});
  const [newSkillRequiredLevels, setNewSkillRequiredLevels] = useState<
    Record<string, Livello>
  >({});
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [query, setQuery] = useState('');
  // Filtro per reparto: `'all'` (tutti) oppure l'`id` di uno specifico reparto.
  const [departmentFilter, setDepartmentFilter] = useState<'all' | string>('all');

  const competencesQuery = useQuery({
    queryKey: competencesKey,
    queryFn: () => api.get<CompetenceDto[]>('/api/competences'),
  });
  const skillsQuery = useQuery({
    queryKey: skillsKey,
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
  });
  const departmentsQuery = useQuery({
    queryKey: departmentsKey,
    queryFn: () => api.get<DepartmentDto[]>('/api/departments'),
  });

  // Le associazioni sono bidirezionali: invalidiamo entrambi i cataloghi.
  const invalidateCatalog = () => {
    void queryClient.invalidateQueries({ queryKey: competencesKey });
    void queryClient.invalidateQueries({ queryKey: skillsKey });
  };

  const createCompetence = useMutation({
    mutationFn: (body: CreateCompetenceRequest) =>
      api.post<CompetenceDto>('/api/competences', body),
    onSuccess: () => {
      setNewName('');
      setNewDescription('');
      setNewMission('');
      setNewLevel1('');
      setNewLevel2('');
      setNewLevel3('');
      setNewLevel4('');
      setNewLevel5('');
      setNewArea('');
      setNewDepartmentId('');
      setNewSkillIds([]);
      setNewSkillWeights({});
      setNewSkillRequiredLevels({});
      setIsCreateOpen(false);
      invalidateCatalog();
    },
  });

  const updateCompetence = useMutation({
    mutationFn: (vars: { id: string; body: UpdateCompetenceRequest }) =>
      api.put<CompetenceDto>(`/api/competences/${vars.id}`, vars.body),
    onSuccess: () => {
      setEditing(null);
      invalidateCatalog();
    },
  });

  const deleteCompetence = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/competences/${id}`),
    onSuccess: () => invalidateCatalog(),
  });

  // --- Import da CSV -------------------------------------------------------
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importDepartmentId, setImportDepartmentId] = useState('');
  const [csvError, setCsvError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportCompetencesResponse | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importCompetences = useMutation({
    mutationFn: (rows: ImportCompetenceRow[]) => {
      const body: ImportCompetencesRequest = {
        competences: rows,
        departmentId: importDepartmentId === '' ? null : importDepartmentId,
      };
      return api.post<ImportCompetencesResponse>('/api/competences/import', body);
    },
    onSuccess: (result) => {
      setImportResult(result);
      invalidateCatalog();
    },
  });

  const openImport = () => {
    setImportResult(null);
    setCsvError(null);
    setImportDepartmentId('');
    importCompetences.reset();
    setIsImportOpen(true);
  };

  const closeImport = () => {
    setIsImportOpen(false);
    setCsvError(null);
    setImportResult(null);
    importCompetences.reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCsvFile = async (file: File) => {
    setCsvError(null);
    setImportResult(null);
    importCompetences.reset();
    try {
      const text = await file.text();
      const rows = parseCompetencesCsv(text);
      importCompetences.mutate(rows);
    } catch (err) {
      setCsvError(
        err instanceof CsvParseError
          ? err.message
          : 'Impossibile leggere il file CSV.',
      );
    }
  };

  const competences = useMemo(
    () => competencesQuery.data ?? [],
    [competencesQuery.data],
  );
  const skills = useMemo(() => skillsQuery.data ?? [], [skillsQuery.data]);

  // Descrizione per skill id: alimenta l'hint "i" accanto al nome nell'editor
  // dei pesi (creazione e modifica competenza).
  const skillDescriptions = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    for (const s of skills) map[s.id] = s.description;
    return map;
  }, [skills]);

  const departments = useMemo(
    () => departmentsQuery.data ?? [],
    [departmentsQuery.data],
  );

  const departmentName = useMemo(() => {
    const byId = new Map(departments.map((d) => [d.id, d.name]));
    return (id: string | null) => (id ? byId.get(id) ?? id : '—');
  }, [departments]);

  // Filtro per reparto + ricerca client‑side per nome o descrizione
  // (case‑insensitive, sottostringa). Il filtro reparto è applicato prima:
  // la ricerca opera quindi all'interno del reparto selezionato.
  const normalizedQuery = query.trim().toLowerCase();
  const visible = useMemo(() => {
    // Il manager vede SOLO le competenze del proprio reparto: lo scoping è
    // applicato a monte e non è aggirabile dal filtro UI (che per lui è
    // bloccato). L'admin usa il `departmentFilter` scelto.
    const scoped =
      managerDepartmentId !== null
        ? competences.filter((c) => c.departmentId === managerDepartmentId)
        : competences;
    const byDepartment =
      departmentFilter === 'all'
        ? scoped
        : scoped.filter((c) => (c.departmentId ?? '') === departmentFilter);
    if (normalizedQuery === '') return byDepartment;
    return byDepartment.filter(
      (c) =>
        c.name.toLowerCase().includes(normalizedQuery) ||
        (c.description ?? '').toLowerCase().includes(normalizedQuery),
    );
  }, [competences, normalizedQuery, departmentFilter, managerDepartmentId]);

  const trimmedNew = newName.trim();
  const trimmedEdit = editing?.name.trim() ?? '';

  // Con un filtro reparto attivo tutte le righe sono della stessa BU: la
  // colonna diventa ridondante e viene nascosta. Lo stesso vale per il manager,
  // le cui competenze appartengono tutte al suo unico reparto.
  const showDepartmentColumn = departmentFilter === 'all' && managerDepartmentId === null;

  const columns = useMemo<Column<CompetenceDto>[]>(
    () => [
      {
        key: 'area',
        header: 'Area',
        render: (c) => <span className="admin-table__assoc">{c.area ?? '—'}</span>,
      },
      {
        key: 'name',
        header: 'Competenza',
        sortable: true,
        sortComparator: compareByName,
        render: (c) => (
          <div className="admin-table__identity">
            <span className="admin-table__name">{c.name}</span>
          </div>
        ),
      },
      ...(showDepartmentColumn
        ? [
            {
              key: 'department',
              header: 'BU',
              render: (c: CompetenceDto) => (
                <span className="admin-table__assoc">
                  {departmentName(c.departmentId)}
                </span>
              ),
            } satisfies Column<CompetenceDto>,
          ]
        : []),
      {
        key: 'skills',
        header: 'Skill associate',
        render: (c) => (
          <span className="admin-table__assoc">{c.skillIds.length}</span>
        ),
      },
      {
        key: 'levels',
        header: 'Livelli',
        render: (c) => <CompetenceLevelBar competence={c} />,
      },
      {
        key: 'actions',
        header: 'Azioni',
        className: 'admin-table__actions-col',
        render: (c) => (
          <div className="admin-item__actions">
            <Button
              type="button"
              size="sm"
              iconOnly
              aria-label={`Modifica ${c.name}`}
              title={`Modifica ${c.name}`}
              onClick={() =>
                setEditing({
                  id: c.id,
                  name: c.name,
                  description: c.description ?? '',
                  mission: c.mission ?? '',
                  level1: c.level1 ?? '',
                  level2: c.level2 ?? '',
                  level3: c.level3 ?? '',
                  level4: c.level4 ?? '',
                  level5: c.level5 ?? '',
                  area: c.area ?? '',
                  departmentId: c.departmentId ?? '',
                  skillIds: [...c.skillIds],
                  skillWeights: { ...c.skillWeights },
                  skillRequiredLevels: { ...c.skillRequiredLevels },
                })
              }
            >
              <Icon name="edit" />
            </Button>
            {/* Eliminazione riservata all'admin (`DELETE` è `requireAdmin`). */}
            {isAdmin && (
              <Button
                type="button"
                size="sm"
                variant="danger"
                iconOnly
                aria-label={`Elimina ${c.name}`}
                title={`Elimina ${c.name}`}
                onClick={() => deleteCompetence.mutate(c.id)}
                loading={deleteCompetence.isPending}
              >
                <Icon name="trash" />
              </Button>
            )}
          </div>
        ),
      },
    ],
    [departmentName, deleteCompetence, showDepartmentColumn, isAdmin],
  );

  return (
    <section className="competence-admin" aria-labelledby="competence-admin-title">
      <PageHeader
        title="Amministrazione — Competenze"
        titleId="competence-admin-title"
        subtitle="Gestisci le competenze e le skill associate (relazione molti-a-molti)."
        actions={
          // Creazione e import sono `requireAdmin`: il manager modifica solo le
          // competenze esistenti del proprio reparto, quindi la toolbar è
          // riservata all'admin.
          isAdmin ? (
            <div className="admin-toolbar">
              <Button type="button" variant="default" onClick={openImport}>
                Importa da CSV
              </Button>
              <Button type="button" variant="primary" onClick={() => setIsCreateOpen(true)}>
                Nuova competenza
              </Button>
            </div>
          ) : undefined
        }
      />

      {/* Ricerca a piena larghezza con filtro business unit integrato (select a
          1/4, input per il resto), come nelle pagine frontend. */}
      <Field
        id="comp-search"
        label="Cerca competenza"
        hint="Filtra per business unit, nome o descrizione. La ricerca opera nella business unit selezionata."
      >
        <div className="competence-search">
          <span className="competence-search__department user-admin__select">
            {managerDepartmentId !== null ? (
              // Il manager è vincolato al proprio reparto: il filtro è bloccato
              // e mostra la sola business unit di competenza (o "—" se non
              // assegnato). La lista è comunque già ristretta in `visible`.
              <select
                id="comp-search-department"
                aria-label="Business unit (bloccata sul tuo reparto)"
                value={managerDepartmentId}
                disabled
              >
                <option value={managerDepartmentId}>
                  {departmentName(managerDepartmentId)}
                </option>
              </select>
            ) : (
              <select
                id="comp-search-department"
                aria-label="Filtra per business unit"
                value={departmentFilter}
                disabled={departmentsQuery.isLoading}
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <option value="all">Tutte le business unit</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            )}
          </span>
          <TextInput
            id="comp-search"
            className="competence-search__input"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per nome o descrizione…"
            autoComplete="off"
            aria-label="Cerca competenza per nome o descrizione"
          />
        </div>
      </Field>

      {/* Import competenze da file CSV (de‑dup per nome lato server). */}
      <Modal
        open={isImportOpen}
        onClose={closeImport}
        title="Importa competenze da CSV"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={closeImport}>
              {importResult ? 'Chiudi' : 'Annulla'}
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={importCompetences.isPending}
              disabled={importCompetences.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              Scegli file CSV…
            </Button>
          </>
        }
      >
        <div className="admin-form">
          <p>
            Carica un file CSV con una colonna <code>name</code> (obbligatoria) e,
            facoltativamente, <code>description</code>, <code>mission</code>,{' '}
            <code>area</code> (Plan/Build/Run/Enable/Manage) e i testi dei livelli{' '}
            <code>level1</code>…<code>level5</code>. Il nome è la chiave: le
            competenze nuove vengono create, quelle già presenti vengono aggiornate su
            descrizione, mission, area e livelli (i campi vuoti nel CSV non sovrascrivono i
            valori esistenti). Se l'area non è indicata, viene dedotta dal prefisso
            e-CF del nome.
          </p>
          <Field
            id="comp-import-department"
            label="Business unit da associare a tutte le competenze importate"
          >
            <span className="user-admin__select user-admin__select--block">
              <select
                id="comp-import-department"
                value={importDepartmentId}
                disabled={importCompetences.isPending}
                onChange={(e) => setImportDepartmentId(e.target.value)}
              >
                <option value="">Nessuna business unit</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </span>
          </Field>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            aria-label="File CSV da importare"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleCsvFile(file);
            }}
          />

          {csvError && <Alert tone="error">{csvError}</Alert>}

          {importCompetences.isError && (
            <Alert tone="error">
              Import non riuscito. Verifica il file e riprova.
            </Alert>
          )}

          {importResult && (
            <Alert
              tone={
                importResult.created > 0 || importResult.updated > 0 ? 'success' : 'info'
              }
            >
              Create {importResult.created} competenze,{' '}
              {importResult.updated} aggiornate
              {importResult.updatedNames.length > 0 && (
                <> ({importResult.updatedNames.join(', ')})</>
              )}
              {importResult.skipped > 0 && (
                <>
                  , {importResult.skipped} saltate
                  {importResult.skippedNames.length > 0 && (
                    <> (duplicati nel CSV: {importResult.skippedNames.join(', ')})</>
                  )}
                </>
              )}
              .
            </Alert>
          )}
        </div>
      </Modal>

      {/* Creazione con associazione N:N alle skill (in modale condivisa). */}
      <Modal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Nuova competenza"
        size="wide"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setIsCreateOpen(false)}>
              Annulla
            </Button>
            <Button
              type="submit"
              form="competence-create-form"
              variant="primary"
              loading={createCompetence.isPending}
              disabled={trimmedNew.length === 0}
            >
              Crea
            </Button>
          </>
        }
      >
        <form
          id="competence-create-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmedNew.length === 0) return;
            const body: CreateCompetenceRequest = {
              name: trimmedNew,
              ...(newDescription.trim() ? { description: newDescription.trim() } : {}),
              ...(newMission.trim() ? { mission: newMission.trim() } : {}),
              ...(newLevel1.trim() ? { level1: newLevel1.trim() } : {}),
              ...(newLevel2.trim() ? { level2: newLevel2.trim() } : {}),
              ...(newLevel3.trim() ? { level3: newLevel3.trim() } : {}),
              ...(newLevel4.trim() ? { level4: newLevel4.trim() } : {}),
              ...(newLevel5.trim() ? { level5: newLevel5.trim() } : {}),
              ...(newArea !== '' ? { area: newArea } : {}),
              departmentId: newDepartmentId === '' ? null : newDepartmentId,
              skillIds: newSkillIds,
              skillWeights: newSkillWeights,
              skillRequiredLevels: newSkillRequiredLevels,
            };
            createCompetence.mutate(body);
          }}
        >
          {/* Nome e Business Unit affiancati su due colonne (una sola su
              viewport strette). */}
          <div className="admin-form__row">
            <Field id="comp-new-name" label="Nome">
              <TextInput
                id="comp-new-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Es. Backend Node.js"
              />
            </Field>
            <Field id="comp-new-department" label="Business Unit">
              <span className="user-admin__select user-admin__select--block">
                <select
                  id="comp-new-department"
                  value={newDepartmentId}
                  onChange={(e) => setNewDepartmentId(e.target.value)}
                >
                  <option value="">Nessuna business unit</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </span>
            </Field>
          </div>
          <div className="admin-form__row">
            <Field id="comp-new-desc" label="Descrizione">
              <TextInput
                id="comp-new-desc"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Descrizione facoltativa"
              />
            </Field>
            <Field id="comp-new-area" label="Area e-CF">
              <span className="user-admin__select user-admin__select--block">
                <select
                  id="comp-new-area"
                  value={newArea}
                  onChange={(e) => setNewArea(e.target.value as '' | CompetenceArea)}
                >
                  <option value="">Nessuna area</option>
                  {COMPETENCE_AREA_VALUES.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
              </span>
            </Field>
          </div>
          <Field id="comp-new-mission" label="Mission">
            <TextArea
              id="comp-new-mission"
              rows={3}
              value={newMission}
              onChange={(e) => setNewMission(e.target.value)}
              placeholder="Mission facoltativa"
            />
          </Field>
          <Field id="comp-new-level1" label="Livello e-1">
            <TextArea
              id="comp-new-level1"
              rows={2}
              value={newLevel1}
              onChange={(e) => setNewLevel1(e.target.value)}
              placeholder="Testo esplicativo del Livello e-1 (facoltativo)"
            />
          </Field>
          <Field id="comp-new-level2" label="Livello e-2">
            <TextArea
              id="comp-new-level2"
              rows={2}
              value={newLevel2}
              onChange={(e) => setNewLevel2(e.target.value)}
              placeholder="Testo esplicativo del Livello e-2 (facoltativo)"
            />
          </Field>
          <Field id="comp-new-level3" label="Livello e-3">
            <TextArea
              id="comp-new-level3"
              rows={2}
              value={newLevel3}
              onChange={(e) => setNewLevel3(e.target.value)}
              placeholder="Testo esplicativo del Livello e-3 (facoltativo)"
            />
          </Field>
          <Field id="comp-new-level4" label="Livello e-4">
            <TextArea
              id="comp-new-level4"
              rows={2}
              value={newLevel4}
              onChange={(e) => setNewLevel4(e.target.value)}
              placeholder="Testo esplicativo del Livello e-4 (facoltativo)"
            />
          </Field>
          <Field id="comp-new-level5" label="Livello e-5">
            <TextArea
              id="comp-new-level5"
              rows={2}
              value={newLevel5}
              onChange={(e) => setNewLevel5(e.target.value)}
              placeholder="Testo esplicativo del Livello e-5 (facoltativo)"
            />
          </Field>
          <Field
            id="comp-new-skills"
            label="Skill associate e peso (1 = marginale, 5 = essenziale)"
          >
            {skillsQuery.isLoading ? (
              <Loading>Caricamento skill…</Loading>
            ) : (
              <SkillWeightEditor
                id="comp-new-skills"
                options={skills}
                descriptions={skillDescriptions}
                selectedIds={newSkillIds}
                weights={newSkillWeights}
                requiredLevels={newSkillRequiredLevels}
                onChangeIds={setNewSkillIds}
                onChangeWeights={setNewSkillWeights}
                onChangeRequiredLevels={setNewSkillRequiredLevels}
              />
            )}
          </Field>
        </form>
      </Modal>

      {/* Modifica di una competenza: nome + descrizione + associazioni N:N. */}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Modifica competenza"
        size="wide"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
              Annulla
            </Button>
            <Button
              type="submit"
              form="competence-edit-form"
              variant="primary"
              loading={updateCompetence.isPending}
              disabled={trimmedEdit.length === 0}
            >
              Salva
            </Button>
          </>
        }
      >
        <form
          id="competence-edit-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!editing || trimmedEdit.length === 0) return;
            const body: UpdateCompetenceRequest = {
              name: trimmedEdit,
              description: editing.description.trim(),
              mission: editing.mission.trim(),
              level1: editing.level1.trim(),
              level2: editing.level2.trim(),
              level3: editing.level3.trim(),
              level4: editing.level4.trim(),
              level5: editing.level5.trim(),
              ...(editing.area !== '' ? { area: editing.area } : {}),
              departmentId: editing.departmentId === '' ? null : editing.departmentId,
              skillIds: editing.skillIds,
              skillWeights: editing.skillWeights,
              skillRequiredLevels: editing.skillRequiredLevels,
            };
            updateCompetence.mutate({ id: editing.id, body });
          }}
        >
          {/* Nome e Business Unit affiancati su due colonne (una sola su
              viewport strette). */}
          <div className="admin-form__row">
            <Field id="comp-edit-name" label="Nome">
              <TextInput
                id="comp-edit-name"
                value={editing?.name ?? ''}
                onChange={(e) =>
                  setEditing((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                }
              />
            </Field>
            <Field
              id="comp-edit-department"
              label="Business Unit"
              hint={
                isAdmin
                  ? undefined
                  : 'Solo un amministratore può cambiare la business unit di una competenza.'
              }
            >
              <span className="user-admin__select user-admin__select--block">
                <select
                  id="comp-edit-department"
                  value={editing?.departmentId ?? ''}
                  // Il manager NON può cambiare il reparto della competenza
                  // (`canChangeCompetenceDepartment` è solo `admin`): il campo è
                  // in sola lettura per lui, così l'edit non tenta di modificarlo
                  // (evitando un 403 lato API).
                  disabled={!isAdmin}
                  onChange={(e) =>
                    setEditing((prev) =>
                      prev ? { ...prev, departmentId: e.target.value } : prev,
                    )
                  }
                >
                  <option value="">Nessuna business unit</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </span>
            </Field>
          </div>
          <div className="admin-form__row">
            <Field id="comp-edit-desc" label="Descrizione">
              <TextInput
                id="comp-edit-desc"
                value={editing?.description ?? ''}
                onChange={(e) =>
                  setEditing((prev) =>
                    prev ? { ...prev, description: e.target.value } : prev,
                  )
                }
                placeholder="Descrizione facoltativa"
              />
            </Field>
            <Field id="comp-edit-area" label="Area e-CF">
              <span className="user-admin__select user-admin__select--block">
                <select
                  id="comp-edit-area"
                  value={editing?.area ?? ''}
                  onChange={(e) =>
                    setEditing((prev) =>
                      prev
                        ? { ...prev, area: e.target.value as '' | CompetenceArea }
                        : prev,
                    )
                  }
                >
                  <option value="">Nessuna area</option>
                  {COMPETENCE_AREA_VALUES.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
              </span>
            </Field>
          </div>
          <Field id="comp-edit-mission" label="Mission">
            <TextArea
              id="comp-edit-mission"
              rows={3}
              value={editing?.mission ?? ''}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, mission: e.target.value } : prev))
              }
              placeholder="Mission facoltativa"
            />
          </Field>
          <Field id="comp-edit-level1" label="Livello e-1">
            <TextArea
              id="comp-edit-level1"
              rows={2}
              value={editing?.level1 ?? ''}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, level1: e.target.value } : prev))
              }
              placeholder="Testo esplicativo del Livello e-1 (facoltativo)"
            />
          </Field>
          <Field id="comp-edit-level2" label="Livello e-2">
            <TextArea
              id="comp-edit-level2"
              rows={2}
              value={editing?.level2 ?? ''}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, level2: e.target.value } : prev))
              }
              placeholder="Testo esplicativo del Livello e-2 (facoltativo)"
            />
          </Field>
          <Field id="comp-edit-level3" label="Livello e-3">
            <TextArea
              id="comp-edit-level3"
              rows={2}
              value={editing?.level3 ?? ''}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, level3: e.target.value } : prev))
              }
              placeholder="Testo esplicativo del Livello e-3 (facoltativo)"
            />
          </Field>
          <Field id="comp-edit-level4" label="Livello e-4">
            <TextArea
              id="comp-edit-level4"
              rows={2}
              value={editing?.level4 ?? ''}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, level4: e.target.value } : prev))
              }
              placeholder="Testo esplicativo del Livello e-4 (facoltativo)"
            />
          </Field>
          <Field id="comp-edit-level5" label="Livello e-5">
            <TextArea
              id="comp-edit-level5"
              rows={2}
              value={editing?.level5 ?? ''}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, level5: e.target.value } : prev))
              }
              placeholder="Testo esplicativo del Livello e-5 (facoltativo)"
            />
          </Field>
          <Field
            id="comp-edit-skills"
            label="Skill associate e peso (1 = marginale, 5 = essenziale)"
          >
            <SkillWeightEditor
              id="comp-edit-skills"
              options={skills}
              descriptions={skillDescriptions}
              selectedIds={editing?.skillIds ?? []}
              weights={editing?.skillWeights ?? {}}
              requiredLevels={editing?.skillRequiredLevels ?? {}}
              onChangeIds={(next) =>
                setEditing((prev) => (prev ? { ...prev, skillIds: next } : prev))
              }
              onChangeWeights={(next) =>
                setEditing((prev) => (prev ? { ...prev, skillWeights: next } : prev))
              }
              onChangeRequiredLevels={(next) =>
                setEditing((prev) =>
                  prev ? { ...prev, skillRequiredLevels: next } : prev,
                )
              }
            />
          </Field>
        </form>
      </Modal>

      {(createCompetence.isError || updateCompetence.isError || deleteCompetence.isError) && (
        <Alert tone="error">Impossibile salvare la modifica. Riprova.</Alert>
      )}

      {competencesQuery.isLoading ? (
        <Loading>Caricamento competenze…</Loading>
      ) : (
        <DataTable
          rows={visible}
          columns={columns}
          rowKey={(c) => c.id}
          error={competencesQuery.isError ? 'Impossibile caricare le competenze.' : null}
          emptyMessage={
            normalizedQuery !== '' || departmentFilter !== 'all'
              ? 'Nessuna competenza corrisponde alla business unit o alla ricerca.'
              : 'Nessuna competenza configurata. Crea la prima con “Nuova competenza”.'
          }
          defaultSort={{ key: 'name', direction: 'asc' }}
          pageSize={10}
        />
      )}
    </section>
  );
}
